import React, {forwardRef} from 'react';
import PropTypes from 'prop-types';

import {
    BasicInput as BaseBasicInput,
    Input as BaseInput,
    useBreakpointValue,
} from '@spothero/ui';

const getInputStyleProps = ({isMobile}) => ({
    fontSize: isMobile ? 'base' : 'sm',
});

/**
 * Input component that uses the fe-ui Input component
 * @type {ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>}
 */
const Input = forwardRef(({includeFormControl, ...props}, ref) => {
    // This is _probably_ not necessary, but there is some inconsistency in the way the responsive styles are applied accross different breakpoints. This is a surefire way to ensure that the correct styles are applied at the correct breakpoints.
    const isMobile = useBreakpointValue({base: true, desktop: false});

    return includeFormControl ? (
        <BaseInput ref={ref} {...props} {...getInputStyleProps({isMobile})} />
    ) : (
        <BaseBasicInput
            ref={ref}
            {...props}
            {...getInputStyleProps({isMobile})}
        />
    );
});

Input.propTypes = {
    /** Whether the input rendered should be the basic or standard (wrapped in the FormControl component) Input component */
    includeFormControl: PropTypes.bool,
    /** The following props are applied to the standard fe-ui Input component (wrapped in the FormControl component) */
    /** The id of the Input. Used to associate the Label and the Input field */
    id: PropTypes.string.isRequired,
    /** The text for Label tag associated with the Input */
    label: PropTypes.string,
    /** The helper text to show above the Input field  */
    helperText: PropTypes.string,
    /** The error message to show below the Input field */
    errorMessage: PropTypes.string,
    /** Wether or not to show the input field in the invalid state */
    isInvalid: PropTypes.bool,
    /** Wether or not to show the input field in the disabled state */
    isDisabled: PropTypes.bool,
    /** Wether or not the Input is required */
    isRequired: PropTypes.bool,
    /** Wether or not the Input is optional */
    isOptional: PropTypes.bool,
    /** A custom string to show when the Input is required */
    customRequiredText: PropTypes.string,
};

Input.defaultProps = {
    includeFormControl: true,
};

export default Input;
